import { ComponentChild } from 'preact'
import { CSSProperties } from 'preact/compat'
import { useState, useEffect } from 'preact/hooks'
import { TextDecorationValue, TextTransformValue } from 'core/helpers/styles'
import { GroupHeading } from 'core/types/element/element_datetime'
import ReactDropdown, { DropdownCustomStyles, Option, OptionState, State } from './ReactDropdown'

type Styles = {
  border_size: number
  border_style: string
  border_color: string
  border_side: boolean[]
  border_top_left: number
  border_top_right: number
  border_bottom_left: number
  border_bottom_right: number
  bg_color: string
  text_color: string
  line_height: number
  text_transform: TextTransformValue
  text_decoration: TextDecorationValue
  letter_spacing: string
  text_shadow: string
  font_family: string
  font_size: number
  font_weight: string
  indicator_color: string
  placeholder_color: string
  menu: {
    bg_color: string
    selected_text_color: string
    selected_bg_color: string
    group_heading?: GroupHeading
  }
}

type SelectWithGroups<T> = {
  label: string
} & (
  | {
      options: readonly T[]
      value?: never
    }
  | {
      value: T
      options?: never
    }
)

type Props<T> = {
  options: T[] | SelectWithGroups<T>[]
  formatOptionLabel: (option: T & OptionState) => ComponentChild
  value?: T | null
  onChange: (val: T) => void
  errorText?: string | null
  includeBlank?: boolean
  readOnly?: boolean
  placeholder?: string
  onMenuOpen?: () => void
  groupBy?: (groupHeading: GroupHeading) => ((acc: Record<string, any>, sess: T) => object) | undefined
  styles: Styles
}

const Dropdown = <T extends Option>(props: Props<T>) => {
  const { options, styles, formatOptionLabel } = props
  const [value, setValue] = useState(props.value)

  const handleChange = (val: T) => {
    setValue(val)
    props.onChange(val)
  }

  useEffect(() => {
    if (props.value) {
      setValue(props.value)
    }
  }, [props.value])

  const customStyles: DropdownCustomStyles<T> = {
    control: (provided: CSSProperties, state: State<T>) => ({
      ...provided,
      border: `${styles.border_size}px ${styles.border_style} rgba(${styles.border_color})`,
      borderTop: styles.border_side[0]
        ? `${styles.border_size}px ${styles.border_style} rgba(${styles.border_color})`
        : 'none',
      borderRight: styles.border_side[1]
        ? `${styles.border_size}px ${styles.border_style} rgba(${styles.border_color})`
        : 'none',
      borderBottom: styles.border_side[2]
        ? `${styles.border_size}px ${styles.border_style} rgba(${styles.border_color})`
        : 'none',
      borderLeft: styles.border_side[3]
        ? `${styles.border_size}px ${styles.border_style} rgba(${styles.border_color})`
        : 'none',
      borderTopLeftRadius: styles.border_top_left,
      borderTopRightRadius: styles.border_top_right,
      borderBottomLeftRadius: state.isOpen ? 0 : styles.border_bottom_left,
      borderBottomRightRadius: state.isOpen ? 0 : styles.border_bottom_right,
      backgroundColor: `rgba(${styles.bg_color})`
    }),
    value: (provided: CSSProperties) => ({
      ...provided,
      color: `rgba(${styles.text_color})`,
      lineHeight: styles.line_height,
      textTransform: styles.text_transform,
      textDecoration: styles.text_decoration,
      letterSpacing: styles.letter_spacing,
      textShadow: styles.text_shadow,
      fontFamily: styles.font_family,
      fontSize: styles.font_size,
      fontWeight: styles.font_weight
    }),
    singleValue: (provided: CSSProperties) => ({
      ...provided,
      color: `rgba(${styles.text_color})`
    }),
    indicator: (provided: CSSProperties) => ({
      ...provided,
      color: `rgba(${styles.indicator_color})`
    }),
    placeholder: (provided: CSSProperties) => ({
      ...provided,
      color: `rgba(${styles.placeholder_color})`
    }),
    menu: (provided: CSSProperties) => ({
      ...provided,
      backgroundColor: `rgba(${styles.menu.bg_color})`
    }),
    option: (provided: CSSProperties, state: OptionState) =>
      state.isSelected || state.isFocused
        ? {
            ...provided,
            color: `rgba(${styles.menu.selected_text_color})`,
            backgroundColor: `rgba(${styles.menu.selected_bg_color})`
          }
        : provided
  }

  const groupStyles = styles.menu.group_heading
  if (groupStyles) {
    customStyles.groupHeading = () => ({
      padding: '8px 12px',
      color: `rgba(${groupStyles.text_color})`,
      backgroundColor: `rgba(${groupStyles.bg_color})`,
      lineHeight: groupStyles.line_height,
      textTransform: groupStyles.text_transform,
      textDecoration: groupStyles.text_decoration,
      letterSpacing: groupStyles.letter_spacing,
      textShadow: groupStyles.text_shadow,
      fontFamily: groupStyles.font_family,
      fontSize: `${groupStyles.font_size}px`,
      fontWeight: groupStyles.font_weight
    })
  }

  return (
    <div className="wf_reset wf_dropdown_wrapper">
      <ReactDropdown
        options={options}
        value={value}
        onChange={handleChange}
        formatOptionLabel={formatOptionLabel}
        customStyles={customStyles}
        errorText={props.errorText}
        includeBlank={props.includeBlank}
        readOnly={props.readOnly}
        placeholder={props.placeholder}
        onMenuOpen={props.onMenuOpen}
      />
    </div>
  )
}

export default Dropdown
